import { z } from "zod";
import type { GameEvent, NationAgentView, NationId, SessionResult } from "@cwb/core";
import { PLAYER_ASSERTION_MAX_LENGTH } from "./embed";
import type { WorldPatch, WorldSnapshot } from "./worldSchemas";

/**
 * Realtime channel (§20.2, §20.3).
 *
 * Client to server carries session membership only; every game action goes over
 * HTTP so there is exactly one validated write path. The one other thing a
 * socket may carry is `session.player`: the portal's signed identity for the
 * run. It is not game state — it cannot change money, stock or territory — and
 * the server only keeps the latest one in memory to hand back to the portal
 * that issued it; the portal, not this server, decides whether it is genuine.
 */

export const SessionJoinSchema = z.object({
  sessionId: z.string().min(1).max(64),
});
export type SessionJoinPayload = z.infer<typeof SessionJoinSchema>;

/**
 * The portal's player assertion for this run, or `null` to withdraw it.
 * `.strict()` so a payload with extra keys is refused rather than half-read.
 */
export const SessionPlayerSchema = z
  .object({
    sessionId: z.string().min(1).max(64),
    assertion: z.string().min(1).max(PLAYER_ASSERTION_MAX_LENGTH).nullable(),
  })
  .strict();
export type SessionPlayerPayload = z.infer<typeof SessionPlayerSchema>;

export const ClientPingSchema = z.object({
  sentAtMs: z.number(),
});
export type ClientPingPayload = z.infer<typeof ClientPingSchema>;

export interface AgentStatusPayload {
  sessionId: string;
  agent: NationAgentView;
}

export interface AgentDecisionPayload {
  sessionId: string;
  nationId: NationId;
  summary: string;
  publicStatement: string;
  /** Human-readable order lines, never raw model output. */
  orderLines: string[];
  turnCount: number;
  tick: number;
}

export interface AgentDoctrinePayload {
  sessionId: string;
  nationId: NationId;
  reason: string;
  additions: string[];
  removals: string[];
  doctrine: string[];
  turnCount: number;
}

export interface EventsPayload {
  sessionId: string;
  events: GameEvent[];
}

export interface SessionEndedPayload {
  sessionId: string;
  result: SessionResult;
}

/** Server to client. */
export interface ServerToClientEvents {
  "world.snapshot": (payload: WorldSnapshot) => void;
  "world.patch": (payload: WorldPatch) => void;
  "world.events": (payload: EventsPayload) => void;
  "agent.status": (payload: AgentStatusPayload) => void;
  "agent.decision": (payload: AgentDecisionPayload) => void;
  "agent.doctrine_updated": (payload: AgentDoctrinePayload) => void;
  "session.ended": (payload: SessionEndedPayload) => void;
  "server.pong": (payload: { sentAtMs: number; serverMs: number }) => void;
  "server.error": (payload: { error: string }) => void;
}

/** Client to server. */
export interface ClientToServerEvents {
  "session.join": (payload: SessionJoinPayload) => void;
  "session.leave": (payload: SessionJoinPayload) => void;
  "session.player": (payload: SessionPlayerPayload) => void;
  "client.ping": (payload: ClientPingPayload) => void;
}

export const SOCKET_EVENTS = {
  snapshot: "world.snapshot",
  patch: "world.patch",
  events: "world.events",
  agentStatus: "agent.status",
  agentDecision: "agent.decision",
  agentDoctrine: "agent.doctrine_updated",
  sessionEnded: "session.ended",
  pong: "server.pong",
  error: "server.error",
  join: "session.join",
  leave: "session.leave",
  player: "session.player",
  ping: "client.ping",
} as const;
